/**
 * OwenAudioBus — Service audio centralisé pour la voix Owen
 * ===========================================================
 *
 * Pourquoi ?
 *   • Avant : 10 `new Audio()` dispersés dans `CoachVocal.js`, 2 dans
 *     `ModuleAudioPlayer.jsx`, 1 dans `PulseBriefingAudioChat.js`. Si une
 *     route change pendant qu'un audio joue ou qu'un fetch TTS est en vol,
 *     certains `HTMLAudioElement` survivaient au démontage (ref oubliée,
 *     race async post-cleanup, etc.) → audio fantôme.
 *
 *   • Maintenant : un singleton enregistre TOUTES les instances `Audio`
 *     créées via `createAudio()`. Une seule fonction `stopAll(reason)`
 *     suffit à garantir l'arrêt de tout audio Owen.
 *
 * Ce que CE bus gère :
 *   • Audios Owen (TTS, welcome, feedback, modules pédagogiques, scénarios)
 *
 * Ce que CE bus NE gère PAS :
 *   • Microphone (`getUserMedia` streams) → géré par `streamRef` côté composant
 *   • LiveKit / WebRTC (visio) → géré par les hooks LiveKit dédiés
 *   • Aucune interférence avec ces flux : seuls les `HTMLAudioElement`
 *     enregistrés via `createAudio()` sont touchés.
 *
 * Distinction des états :
 *   • Audio en lecture (`!audio.paused`) → ciblé par `stopAll()`
 *   • Audio jamais joué / déjà ended → enregistré mais `stopAll()` est idempotent
 *   • Owen idle / standby (aucun audio dans le bus) → `stopAll()` no-op
 */

// Always-on debug flag: enabled when running in a browser (no `process` global)
// or explicitly when NODE_ENV !== 'production' on the build side.
const DEBUG =
  (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') ||
  (typeof window !== 'undefined' && typeof process === 'undefined');

class OwenAudioBus {
  constructor() {
    /** @type {Set<HTMLAudioElement>} */
    this._audios = new Set();
    /** @type {Array<(audio: HTMLAudioElement|null, reason: string) => void>} */
    this._listeners = [];
    this._counter = 0;
  }

  /**
   * Crée un HTMLAudioElement + l'enregistre automatiquement.
   * À utiliser à la place de `new Audio(url)`.
   *
   * @param {string} url
   * @param {object} [opts]
   * @param {string} [opts.label]  Étiquette debug (ex: "tts-welcome").
   * @returns {HTMLAudioElement}
   */
  createAudio(url, opts = {}) {
    const audio = new Audio(url);
    audio.__owenLabel = opts.label || `audio-${++this._counter}`;
    this.register(audio);
    // Auto-unregister sur fin de cycle de vie
    const release = () => this.unregister(audio);
    audio.addEventListener('ended', release, { once: true });
    audio.addEventListener('error', release, { once: true });
    return audio;
  }

  /**
   * Enregistre un audio déjà créé ailleurs (ex: librairie externe).
   * @param {HTMLAudioElement} audio
   */
  register(audio) {
    if (!audio || this._audios.has(audio)) return;
    this._audios.add(audio);
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log(`[OwenAudioBus] + register "${audio.__owenLabel || 'unlabeled'}" (active=${this._audios.size})`);
    }
    this._notify(audio, 'register');
  }

  /**
   * Retire un audio du registry (sans le stopper).
   * @param {HTMLAudioElement} audio
   */
  unregister(audio) {
    if (!audio || !this._audios.has(audio)) return;
    this._audios.delete(audio);
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log(`[OwenAudioBus] - unregister "${audio.__owenLabel || 'unlabeled'}" (active=${this._audios.size})`);
    }
    this._notify(audio, 'unregister');
  }

  /**
   * Stoppe TOUS les audios enregistrés. Idempotent.
   * @param {string} [reason]  Pour debug / observability.
   */
  stopAll(reason = 'manual') {
    if (this._audios.size === 0) {
      // Owen idle : rien à stopper, opération no-op silencieuse.
      return 0;
    }
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log(`[OwenAudioBus] 🛑 stopAll(reason="${reason}") — ${this._audios.size} audio(s)`);
    }
    let stopped = 0;
    // Snapshot avant itération (les handlers peuvent muter le set)
    for (const audio of [...this._audios]) {
      try {
        // Pause immédiate
        if (!audio.paused) {
          audio.pause();
          stopped++;
        }
        // Détacher la source pour libérer le buffer et empêcher tout
        // resume automatique (mobile : `media keys`, lockscreen, etc.).
        try { audio.src = ''; } catch (_) { /* noop */ }
        // Nettoyer les handlers — évite onended/onplay/onerror callbacks
        // qui pourraient remettre l'UI en état "Owen parle"
        audio.onplay = null;
        audio.onended = null;
        audio.onerror = null;
        audio.onloadedmetadata = null;
        audio.ondurationchange = null;
        audio.ontimeupdate = null;
      } catch (_) { /* noop, ne pas casser le batch */ }
    }
    this._audios.clear();
    this._notify(null, reason);
    return stopped;
  }

  /**
   * Pause TOUS les audios actifs SANS détacher la source (page hidden).
   * Le user peut reprendre via les contrôles UI existants au retour.
   */
  pauseAll(reason = 'visibility_hidden') {
    if (this._audios.size === 0) return 0;
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log(`[OwenAudioBus] ⏸ pauseAll(reason="${reason}") — ${this._audios.size} audio(s)`);
    }
    let paused = 0;
    for (const audio of this._audios) {
      try {
        if (!audio.paused) {
          audio.pause();
          paused++;
        }
      } catch (_) { /* noop */ }
    }
    this._notify(null, reason);
    return paused;
  }

  /** Compteur d'audios actuellement enregistrés (UI / debug). */
  getActiveCount() {
    return this._audios.size;
  }

  /**
   * S'abonner aux événements du bus (UI badge "Owen parle" globale).
   * @param {(audio: HTMLAudioElement|null, reason: string) => void} fn
   * @returns {() => void} unsubscribe
   */
  subscribe(fn) {
    this._listeners.push(fn);
    return () => {
      this._listeners = this._listeners.filter(l => l !== fn);
    };
  }

  _notify(audio, reason) {
    for (const fn of this._listeners) {
      try { fn(audio, reason); } catch (_) { /* noop */ }
    }
  }
}

// Singleton (module-level)
const OwenAudioBusInstance = new OwenAudioBus();

// Toujours exposé sur window pour tests E2E et debug console
// (lecture-seule sémantique : utilité = appel ponctuel stopAll/getActiveCount).
if (typeof window !== 'undefined') {
  window.__OwenAudioBus = OwenAudioBusInstance;
}

export default OwenAudioBusInstance;
